'use client'

import { X } from 'lucide-react'
import { motion } from 'framer-motion'
import type { PanInfo } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import DeleteConfirmation from '../../../ui/delete-confirmation'
import { formatPrice } from '~/utils/format'
import type { RoomBooking } from '~/types/booking'
import GuestSelector from './guest-selector'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { getImageUrlCrs } from '~/utils/url-image-crs'
import { Image } from '~/components/ui/image'
import BookingSummaryExtraService from '~/components/booking/desktop/list/booking-summary-extra-service'

interface SwipeableRoomItemProps {
  room: RoomBooking
  roomNumber: number
  onDelete: (cartItemId: string) => void
  editingRoomId: string | null
  setEditingRoomId: (id: string | null) => void
  handleGuestChange: (
    cartItemId: string,
    type: 'adults' | 'children6To12' | 'childrenUnder6',
    value: number
  ) => void
}

function SwipeableRoomItem({
  room,
  roomNumber,
  onDelete,
  editingRoomId,
  setEditingRoomId,
  handleGuestChange,
}: SwipeableRoomItemProps) {
  const [isSwiped, setIsSwiped] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const imageUrl = room.imageUrl || '/images/room-placeholder.jpg'

  const handleDragEnd = (_: any, dragInfo: PanInfo) => {
    const SWIPE_THRESHOLD = 40
    const VELOCITY_THRESHOLD = 100

    if (isSwiped) {
      if (dragInfo.offset.x > 10) {
        setIsSwiped(false)
      } else {
        setIsSwiped(!isSwiped)
      }
    } else {
      const draggedFarEnough = dragInfo.offset.x < -SWIPE_THRESHOLD
      const draggedFastEnough = dragInfo.velocity.x < -VELOCITY_THRESHOLD

      if (draggedFarEnough || draggedFastEnough) {
        setIsSwiped(true)
      }
    }
  }

  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
  //       setIsSwiped(false)
  //     }
  //   }

  //   document.addEventListener('mousedown', handleClickOutside)
  //   return () => document.removeEventListener('mousedown', handleClickOutside)
  // }, [])

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false)
    onDelete(room.cartItemId)
    setIsSwiped(false)
  }

  const roomKey = `${room.cartItemId}-${roomNumber}`

  // Tính toán số lượng trẻ em tối đa cho mỗi loại
  const getMaxChildren6To12 = () => {
    const remainingSlots = room.maxChildren - room.childrenUnder6
    return Math.max(0, remainingSlots)
  }

  const getMaxChildrenUnder6 = () => {
    const remainingSlots = room.maxChildren - room.children6To12
    return Math.max(0, remainingSlots)
  }

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
      {/* Background nút xóa */}
      <div className="absolute inset-y-0 right-0 z-0 flex w-[60px] items-center justify-center rounded-r-lg bg-red-600">
        <button
          type="button"
          onClick={handleDeleteClick}
          className="text-white"
          aria-label={t('booking.deleteRoom')}
        >
          <svg
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.4622 12.3603C14.4017 13.3497 14.3536 14.1348 14.2551 14.7619C14.1541 15.4051 13.9924 15.9407 13.669 16.4091C13.3731 16.8377 12.9922 17.1995 12.5504 17.4713C12.0675 17.7684 11.5282 17.8984 10.8864 17.9605L6.05031 17.9604C5.40776 17.8982 4.86793 17.768 4.38468 17.4703C3.9426 17.1979 3.56153 16.8356 3.26574 16.4063C2.94241 15.937 2.78122 15.4006 2.68097 14.7566C2.58322 14.1286 2.53623 13.3424 2.47701 12.3516L1.96289 3.75H14.9892L14.4622 12.3603ZM6.50187 14.1797C6.17486 14.1797 5.90977 13.9146 5.90977 13.5876L5.90977 8.85074C5.90977 8.52373 6.17486 8.25864 6.50187 8.25864C6.82888 8.25864 7.09398 8.52373 7.09398 8.85074L7.09398 13.5876C7.09398 13.9146 6.82888 14.1797 6.50187 14.1797ZM11.0409 8.85089C11.0409 8.52388 10.7758 8.25879 10.4487 8.25879C10.1217 8.25879 9.85664 8.52388 9.85664 8.85089L9.85664 13.5877C9.85664 13.9147 10.1217 14.1798 10.4487 14.1798C10.7758 14.1798 11.0409 13.9147 11.0409 13.5877L11.0409 8.85089Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.53847 1.01269C9.98459 1.05259 10.4039 1.18873 10.764 1.45727C11.0302 1.65588 11.2153 1.89899 11.3733 2.16228C11.5197 2.40627 11.6672 2.71047 11.8344 3.05556L12.1713 3.75053H15.5801C16.0161 3.75053 16.3696 4.10399 16.3696 4.54C16.3696 4.97602 16.0161 5.32948 15.5801 5.32948C10.843 5.32948 6.10655 5.32948 1.36955 5.32948C0.933538 5.32948 0.580078 4.97602 0.580078 4.54C0.580078 4.10399 0.933538 3.75053 1.36955 3.75053H4.85094L5.13183 3.13431C5.29491 2.77652 5.43852 2.46141 5.58279 2.20856C5.7384 1.93582 5.92262 1.68342 6.19214 1.47665C6.55664 1.19702 6.9844 1.05525 7.44054 1.01373C7.78396 0.982464 8.13016 0.986767 8.47482 0.987321C8.87807 0.987969 9.24058 0.986043 9.53847 1.01269ZM6.58621 3.75053H10.4167C10.2377 3.38153 10.1232 3.14766 10.0195 2.97487C9.8677 2.72198 9.68583 2.61112 9.39781 2.58536C9.19311 2.56705 8.92634 2.56632 8.50207 2.56632C8.06722 2.56632 7.79348 2.56707 7.58369 2.58617C7.28835 2.61306 7.1042 2.72815 6.95422 2.99102C6.85593 3.16329 6.74892 3.3939 6.58621 3.75053Z"
              fill="white"
            />
          </svg>
        </button>
      </div>

      {/* Nội dung vuốt được */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onDragEnd={handleDragEnd}
        animate={{ x: isSwiped ? -80 : 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        className="relative z-10 w-full touch-pan-y bg-white"
      >
        <div className="flex flex-col gap-4 p-2">
          <div className="flex flex-col items-start gap-1">
            <div className="flex w-full justify-between gap-1">
              <div className="flex items-start gap-2">
                <Image
                  src={getImageUrlCrs(imageUrl, 'thumbnail')}
                  alt={room.name}
                  loading="lazy"
                  fill
                  objectFit="contain"
                  className="image-room h-10 w-10 flex-shrink-0 rounded-lg"
                />
                <div className="flex flex-col items-start">
                  <h4 className="whitespace-nowrap font-lexend text-sm font-medium text-gray-900">
                    {t('booking.room')} {roomNumber}
                  </h4>
                  <p className="mt-1 whitespace-nowrap font-lexend text-xs font-[350] leading-4 text-secondary-950">
                    1x {room.name}
                  </p>
                </div>
              </div>
              <span className="whitespace-nowrap font-lexend text-xs font-medium leading-4 text-secondary-950">
                {formatPrice(room.price)}
              </span>
            </div>
            <div className="mt-2 flex w-full items-center justify-between">
              <div>
                <p
                  className={`whitespace-nowrap font-lexend text-[10px] font-[350] ${room.adults > room.maxAdults || room.children6To12 + room.childrenUnder6 > room.maxChildren ? 'text-red-500' : 'text-[#666B74]'}`}
                >
                  {room.adults} {t('booking.adults')}, {room.children6To12 + room.childrenUnder6}{' '}
                  {t('booking.children')}
                </p>
                <p className="whitespace-nowrap font-lexend text-[10px] font-[350] text-[#666B74]">
                  {t('booking.maxGuests')} {room.adults} {t('booking.adults')},{' '}
                  {room.children6To12 + room.childrenUnder6} {t('booking.children')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingRoomId(editingRoomId === roomKey ? null : roomKey)}
                className="whitespace-nowrap font-lexend text-[10px] text-primary-500 hover:text-primary-600"
              >
                {t('booking.edit')}
              </button>
            </div>
          </div>

          {editingRoomId === roomKey && (
            <>
              <div className="space-y-4 rounded-lg border border-[#F3F3F3] p-2">
                <GuestSelector
                  label={t('booking.adults')}
                  description={t('booking.adultsDesc')}
                  value={room.adults}
                  min={1}
                  max={room.maxAdults}
                  onChange={(val) => handleGuestChange(room.cartItemId, 'adults', val)}
                />
                <GuestSelector
                  label={t('booking.childrenOver6')}
                  description={t('booking.childrenOver6Desc')}
                  value={room.children6To12}
                  min={0}
                  max={getMaxChildren6To12()}
                  onChange={(val) => handleGuestChange(room.cartItemId, 'children6To12', val)}
                />
                <GuestSelector
                  label={t('booking.childrenUnder6')}
                  description={t('booking.childrenUnder6Desc')}
                  value={room.childrenUnder6}
                  min={0}
                  max={getMaxChildrenUnder6()}
                  onChange={(val) => handleGuestChange(room.cartItemId, 'childrenUnder6', val)}
                />
              </div>
              <BookingSummaryExtraService
                extra_guest_desc={room.extraGuestDesc}
              ></BookingSummaryExtraService>
            </>
          )}
        </div>
      </motion.div>

      {/* Modal xác nhận xóa */}
      <DeleteConfirmation
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

export default SwipeableRoomItem
